import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_INBOUND } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import Search from "@/design/components/Search";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputCopy from "@/design/forms/InputCopy";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { any, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viArrowRightToBracket, viPlus, viXmark } from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import inboundReducer from "./inboundReducer";
import AddWebhookItem from "./webhooks/AddWebhookItem";
import WebhookItem from "./webhooks/WebhookItem";

function Inbound() {
  const navigate = useNavigate();
  const { did } = useParams();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const [forceCallerId, setForceCallerId] = useState(false);

  const pbx = useSelector((state) => state.domain.pbx);
  const phoneNumberStore = useSelector((state) => state.domain.phoneNumbers);
  const webhookStore = useSelector((state) => state.domain.webhooks);
  const distinctiveRingtonesStore = useSelector((state) => state.domain.distinctiveRingtones);

  // Get general data
  useEffect(() => {
    Promise.all([Api.getAvailablePhoneNumbers(), Api.getWebhooks(), Api.getDistinctiveRingtones()]);
  }, []);

  const {
    value: inbound,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_INBOUND,
    reducer: inboundReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
      did: string().required({ message: "Number is required." }),
      overflow: any().required({ message: "Destination is required." }).not(0, { message: "Destination cannot be 0." }),
      forceCallerId: string()
        .pattern("^[-'a-zA-Z0-9À-ÖØ-öø-ÿ\\ \\,.+_\"–]*$", {
          message: "The Caller ID can only contain alphanumeric characters, spaces, commas, dots, and dashes.",
        })
        .length([1, 40], {
          message: "The Caller ID must be at most 40 characters.",
        }),
    }),
    onSubmit: () => Api.storeInbound(inbound, did === "new"),
    onSuccess: () => {
      if (did === "new") {
        Promise.resolve(Api.getAvailablePhoneNumbers());
        navigate(`../${inbound.did}`);
      }

      Promise.resolve(Api.getInbounds());
    },
  });

  const setInbound = useEffectEvent((value) => dispatch({ type: "set", value }));

  useEffect(() => {
    let ignore = false;

    if (!did) {
      return;
    }

    if (did === "new") {
      Api.cancelActiveGetRequests();
      startTransition(() => {
        setInbound(NEW_INBOUND);
        setForceCallerId(false);
      });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getInbound({ did }))
      .then((response) => {
        if (!ignore) {
          setInbound(response.data.inboundRoute);
          setForceCallerId(response.data.inboundRoute.forceCallerId ? true : false);
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [did]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "forceCaller":
        dispatch({ type: property, value });
        setForceCallerId(!forceCallerId);
        break;
      default:
        dispatch({ type: property, value });
        break;
    }

    setSaveState(SaveState.UNSAVED);
  };

  // Webhook drawer
  const availableWebhooks = useMemo(
    () =>
      webhookStore.list.filter(
        (_webhook) =>
          !_webhook.useNewWebhooks && !inbound.webhooks.find((currentWebhooks) => currentWebhooks.id === _webhook.id),
      ),
    [webhookStore.list, inbound.webhooks],
  );

  const [drawer, setDrawer] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const searchResults = useSearch(availableWebhooks, searchValue, ["name"]);

  const closeDrawer = useCallback(() => {
    setDrawer(undefined);
    setSearchValue("");
  }, []);

  // Delete
  function onDeleteFinish() {
    Promise.all([Api.getAvailablePhoneNumbers(), Api.getInbounds()]).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${inbound.name || "Inbound route"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={inbound.name} icon={viArrowRightToBracket} />
              </IconButton>
            ) : (
              <Avatar text={inbound.name} icon={viArrowRightToBracket} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                key={inbound.did || "nk"}
                isActionable={did !== "new"}
                onDelete={() => Api.deleteInbound(inbound)}
                name={`${inbound.name} (${inbound.did})`}
                deleteInfo="Are you sure you want to delete this inbound? You will no longer be able to receive incoming calls on this number."
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={inbound.name || "Unnamed inbound route"}
          subheader={inbound.did || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={inbound.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            {did === "new" ? (
              <InputAutocomplete
                required
                id="did"
                label="Number"
                onChange={formHandler}
                value={inbound.did}
                selectValue="did"
                selects={phoneNumberStore.list}
                getOptionLabel={(option) => option.did}
                error={errors}
                helperText="You can't change this after saving this inbound route."
              />
            ) : (
              <InputCopy
                id="number"
                label="Number"
                value={inbound.did}
                helperText="You can't change the number for an existing inbound route."
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputAutocomplete
              id="distinctiveRingtoneId"
              label="Distinctive ringtone"
              onChange={formHandler}
              value={inbound.distinctiveRingtoneId}
              selectValue="id"
              selects={distinctiveRingtonesStore.list}
              getOptionLabel={(option) => option.name}
              hasEmpty={null}
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Settings">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputSelect
              required
              id="callRecording"
              label="Recording"
              onChange={formHandler}
              value={inbound.callRecording}
              selectValue="value"
              selects={[
                { value: "undefined", title: "Undefined" },
                { value: "no", title: "Off" },
                { value: "yes", title: "On" },
              ]}
              getOptionLabel={(option) => option.title}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <OverflowDrawer
              required
              label="Destination"
              value={inbound.overflow}
              propertyName="overflow"
              overflowHandler={(value) => formHandler("overflow", value)}
              filter={{ exten: [inbound.exten], custom: [], sound: [] }}
              helperText="Set the destination if the inbound route does not pick up the call."
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Caller ID">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="forceCaller"
              onChange={formHandler}
              states={[false, true]}
              value={forceCallerId}
              primary="Force Caller ID"
              secondary="Force the Caller ID that will be displayed."
              error={errors}
            />
          </Grid>
          {forceCallerId && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputText
                required
                id="forceCallerId"
                label="Caller ID"
                onChange={formHandler}
                value={inbound.forceCallerId}
                error={errors}
              />
            </Grid>
          )}
        </FormGroup>
        <FormGroup
          title="Webhooks"
          actions={
            <Button
              onClick={() => setDrawer(true)}
              disabled={did === "new"}
              startIcon={<Valkyrie icon={viPlus} />}
              variant="contained"
            >
              Add
            </Button>
          }
        >
          <Grid size={12}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                gap: 1,
              }}
            >
              {inbound.webhooks.length ? (
                inbound.webhooks.map((webhook) => (
                  <WebhookItem key={webhook.id} inbound={inbound} webhook={webhook} dispatch={dispatch} />
                ))
              ) : (
                <Typography>No linked webhooks.</Typography>
              )}
            </Box>
            <Drawer
              open={!!drawer}
              onClose={() => closeDrawer()}
              anchor="right"
              slotProps={{
                paper: {
                  sx: (theme) => ({ maxWidth: 320, width: 1, p: 1.5, display: "flex", gap: 1, ...theme.scrollbar }),
                  elevation: 4,
                },
              }}
            >
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h5">Add webhook</Typography>
                <IconButton onClick={() => closeDrawer()} color="inherit">
                  <Valkyrie icon={viXmark} />
                </IconButton>
              </Stack>
              <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
                <Search blur value={searchValue} onChange={setSearchValue} />
              </Box>
              <List>
                {searchResults.length >= 1 ? (
                  searchResults.map((webhook) => (
                    <AddWebhookItem key={webhook.id} webhook={webhook} dispatch={dispatch} inbound={inbound} />
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="There are no webhooks available..." />
                  </ListItem>
                )}
              </List>
            </Drawer>
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Inbound;
